import React, { FC, MouseEvent } from 'react';
import { COLORS } from '../journey-explorer-utils';
import { MapEdge } from '../journey-map-layout';
import { formatPct } from '../journey-compare';

export const ARROW_ID = 'journey-explorer-arrow';

interface MapTransitionProps {
	edge: MapEdge;
	label: string;
	width: number;
	selected: boolean;
	filtered: boolean;
	focused: boolean;
	faded: boolean;
	showGoal: boolean;
	compareA?: number | null;
	compareB?: number | null;
	onSelect: (edge: MapEdge) => void;
}

const stopPropagation = (event: MouseEvent) => event.stopPropagation();

const MapTransition: FC<MapTransitionProps> = ({
	edge,
	label,
	width,
	selected,
	filtered,
	focused,
	faded,
	showGoal,
	compareA,
	compareB,
	onSelect,
}) => {
	const comparing = compareA !== undefined;
	const share = comparing
		? `${formatPct(compareA)} · ${formatPct(compareB)}`
		: showGoal
		? `${edge.stat.reached_pct.toFixed(0)}%`
		: '';
	const chipWidth = Math.max(label.length, share.length) * 5.9 + 12;
	const chipX = edge.anchor === 'start' ? edge.label.x - 3 : edge.label.x - chipWidth / 2;
	const textX = edge.anchor === 'start' ? edge.label.x + 2 : edge.label.x;
	const stroke = selected || filtered ? COLORS.text : focused ? COLORS.muted : COLORS.line;

	return (
		<g
			opacity={faded ? 0.15 : 1}
			style={{ cursor: 'pointer' }}
			onMouseDown={stopPropagation}
			onClick={(event) => {
				event.stopPropagation();
				onSelect(edge);
			}}
		>
			<path d={edge.path} fill="none" stroke="transparent" strokeWidth={14} />
			<path d={edge.path} fill="none" stroke={stroke} strokeWidth={width} markerEnd={`url(#${ARROW_ID})`} />
			<rect
				x={chipX}
				y={edge.label.y - 15}
				width={chipWidth}
				height={27}
				rx={5}
				fill={COLORS.card}
				opacity={0.92}
			/>
			<text
				x={textX}
				y={edge.label.y - 4}
				textAnchor={edge.anchor}
				fill={COLORS.muted}
				fontSize={9.5}
				fontWeight={500}
			>
				{label}
			</text>
			{comparing ? (
				<text x={textX} y={edge.label.y + 7} textAnchor={edge.anchor} fontSize={9.5} fontWeight={600}>
					<tspan fill={COLORS.compareA}>{formatPct(compareA)}</tspan>
					<tspan fill={COLORS.dim}> · </tspan>
					<tspan fill={COLORS.compareB}>{formatPct(compareB)}</tspan>
				</text>
			) : (
				<text
					x={textX}
					y={edge.label.y + 7}
					textAnchor={edge.anchor}
					fill={COLORS.goal}
					fontSize={9.5}
					fontWeight={500}
					opacity={0.9}
				>
					{share}
				</text>
			)}
		</g>
	);
};

export default React.memo(MapTransition);
